import { Box, SxProps, Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import type { CancellationPolicy } from '../models/CancellationPolicy';
import {
  CancellationPolicyTone,
  resolveCancellationPolicyDisplay,
} from '../utils/formatCancellationPolicy';

type BookingCancellationPolicyProps = {
  startTime: string;
  policy: CancellationPolicy | null | undefined;
  /** Booking display timezone from the API (IANA), e.g. Europe/Warsaw. */
  timezone?: string | null;
  /** When false, hide the row if policy is missing. Default true (show unavailable). */
  showWhenMissing?: boolean;
  /** Dense list rows vs detail/dialog cue. */
  density?: 'compact' | 'comfortable';
  /** Filled accent band. Turn off on tight steps so the line is just icon and text. */
  surface?: boolean;
  /**
   * Series checkout. When greater than 1, the dated deadline is the first
   * session (later sessions keep the same hour window from their own start).
   */
  sessionCount?: number;
  sx?: SxProps<Theme>;
};

function toneColor(theme: Theme, tone: CancellationPolicyTone): string {
  switch (tone) {
    case 'free':
      return theme.palette.success.main;
    case 'partial':
      return theme.palette.warning.main;
    case 'none':
      return theme.palette.text.secondary;
    case 'unavailable':
    default:
      return theme.palette.text.disabled;
  }
}

function ToneIcon({ tone }: { tone: CancellationPolicyTone }) {
  const sx = { fontSize: 16, flexShrink: 0, mt: '1px' };
  if (tone === 'none') return <BlockOutlinedIcon sx={sx} />;
  if (tone === 'unavailable') return <HelpOutlineOutlinedIcon sx={sx} />;
  return <EventAvailableOutlinedIcon sx={sx} />;
}

/**
 * Quiet deadline ledger for org cancellation rules.
 * Emphasizes the absolute free-cancellation cutoff when present.
 */
export default function BookingCancellationPolicy({
  startTime,
  policy,
  timezone,
  showWhenMissing = true,
  density = 'compact',
  surface = true,
  sessionCount = 1,
  sx,
}: BookingCancellationPolicyProps) {
  const { t, i18n } = useTranslation();

  if (!policy && !showWhenMissing) {
    return null;
  }

  const display = resolveCancellationPolicyDisplay(
    startTime,
    policy,
    t,
    i18n.language,
    timezone,
    new Date(),
    sessionCount,
  );
  const leadText =
    display.scopeNote && !/[.!?…]$/.test(display.fullText.trimEnd())
      ? `${display.fullText.trimEnd()}.`
      : display.fullText;
  const accessibleText = display.scopeNote
    ? `${leadText} ${display.scopeNote}`
    : display.fullText;

  const comfortable = density === 'comfortable';

  return (
    <Box
      role="note"
      aria-label={accessibleText}
      sx={[
        (theme) => {
          const accent = toneColor(theme, display.tone);
          return {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            color: accent,
            ...(surface
              ? {
                  px: comfortable ? 1.5 : 1.25,
                  py: comfortable ? 1.15 : 0.85,
                  borderRadius: 1.5,
                  borderLeft: '3px solid',
                  borderColor: accent,
                  bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.12 : 0.07),
                }
              : {
                  px: 0,
                  py: 0,
                }),
          };
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <ToneIcon tone={display.tone} />
      <Typography
        variant={comfortable ? 'body2' : 'caption'}
        component="p"
        sx={{
          m: 0,
          lineHeight: 1.45,
          color: 'text.secondary',
          letterSpacing: comfortable ? '0.01em' : 0,
        }}
      >
        {display.deadline ? (
          <>
            {display.lead}
            {display.lead ? ' ' : null}
            <Box
              component="span"
              sx={{
                display: 'inline',
                fontWeight: 700,
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {display.deadline}
            </Box>
            {display.trail}
          </>
        ) : (
          leadText
        )}
        {display.scopeNote ? ` ${display.scopeNote}` : null}
      </Typography>
    </Box>
  );
}
